import React, { useEffect, useState } from "react";
import { api, LITURGY_LABEL } from "@/lib/api";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const dowLabels = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const CalendarPage = () => {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [days, setDays] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/liturgical/month`, { params: { year, month } }).then(r => setDays(r.data.days)).finally(() => setLoading(false));
    }, [year, month]);

    const first = new Date(year, month - 1, 1);
    const startPad = first.getDay(); // 0=Sun

    const goPrev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
    const goNext = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

    const colorClass = {
        green: "bg-liturgy-green",
        violet: "bg-liturgy-violet",
        white: "bg-liturgy-white",
        red: "bg-liturgy-red",
        rose: "bg-liturgy-rose",
        black: "bg-liturgy-black",
    };

    return (
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="calendar-page">
            <div className="text-center mb-10">
                <div className="small-caps text-xs text-muted-foreground mb-3">The Liturgical Year</div>
                <h1 className="font-serif text-4xl md:text-5xl">{monthNames[month-1]} {year}</h1>
            </div>

            <div className="flex items-center justify-center gap-6 mb-8">
                <button onClick={goPrev} data-testid="cal-prev" className="p-2 hover:accent-liturgy"><ChevronLeft /></button>
                <button onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth()+1); }} data-testid="cal-today" className="small-caps text-xs px-4 py-2 border border-liturgy accent-liturgy hover:bg-liturgy hover:text-white transition-colors">Today</button>
                <button onClick={goNext} data-testid="cal-next" className="p-2 hover:accent-liturgy"><ChevronRight /></button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2 text-center small-caps text-[10px] text-muted-foreground">
                {dowLabels.map(d => <div key={d}>{d}</div>)}
            </div>

            {loading ? (
                <div className="text-center py-24 text-muted-foreground">Loading the calendar…</div>
            ) : (
                <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
                    {Array.from({length: startPad}).map((_, i) => <div key={"pad-"+i} />)}
                    {days.map(d => {
                        const dNum = parseInt(d.date.split("-")[2], 10);
                        const isToday = d.date === now.toISOString().slice(0,10);
                        return (
                            <Link key={d.date} to={`/day/${d.date}`} data-testid={`day-${d.date}`}
                                className={`aspect-square missal-card p-2 flex flex-col hover-lift ${isToday ? 'ring-1 ring-liturgy' : ''}`}>
                                <div className="flex items-start justify-between">
                                    <span className={`font-serif text-lg ${isToday ? 'accent-liturgy' : ''}`}>{dNum}</span>
                                    <span className="w-2 h-2 rounded-full" style={{ background: `hsl(${({green:'138 30% 30%',violet:'266 40% 40%',white:'44 55% 55%',red:'0 60% 34%',rose:'345 40% 62%',black:'0 0% 15%'})[d.color]})` }} />
                                </div>
                                {d.feast && <div className="mt-1 text-[10px] leading-tight text-muted-foreground line-clamp-3">{d.feast}</div>}
                            </Link>
                        );
                    })}
                </div>
            )}

            <div className="mt-10 flex items-center justify-center gap-6 flex-wrap text-xs">
                {["green","violet","white","red","rose","black"].map(c => (
                    <div key={c} className="flex items-center gap-2 text-muted-foreground">
                        <span className="w-3 h-3 rounded-full" style={{ background: `hsl(${({green:'138 30% 30%',violet:'266 40% 40%',white:'44 55% 55%',red:'0 60% 34%',rose:'345 40% 62%',black:'0 0% 15%'})[c]})` }} />
                        {LITURGY_LABEL[c]}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CalendarPage;
